import { z } from 'zod';
/**
 * The tenant's survey question toggles, split the way the readers need them —
 * a wire shape as well as a type, because the survey dialog reads them off the
 * server's response rather than off its own settings query (see
 * `GetSurveyResponseSchema`).
 * `classic` is the OR over the three question toggles and `variantCheck` is the
 * Variant Check switch (ADR 0022): the two groups answer different questions,
 * so which one a surface reads is the surface's own decision — the
 * post-finalize redirect wants `classic` alone, because the recorder authored
 * the step and the Variant Check never applies to an author.
 */ export const SurveyTogglesSchema = z.object({
    rating: z.boolean(),
    frequency: z.boolean(),
    duration: z.boolean(),
    /** Any of the three classic question toggles. */ classic: z.boolean(),
    /** The Variant Check (ADR 0022) — asked of every recipient but the author. */ variantCheck: z.boolean()
});
/**
 * The single derivation, shared by both sides of the wire, so no caller
 * re-derives the classic OR by hand.
 */ export function readSurveyToggles(settings) {
    const rating = Boolean(settings?.enableRecordingSurvey);
    const frequency = Boolean(settings?.enableFrequencySurvey);
    const duration = Boolean(settings?.enableDurationSurvey);
    return {
        rating,
        frequency,
        duration,
        classic: rating || frequency || duration,
        variantCheck: Boolean(settings?.enableVariantCheck)
    };
}
/**
 * Whether a pending survey has anything to ask THIS recipient (ADR 0022). The
 * classic questions apply to everyone; the Variant Check verdict is the only
 * thing left for an author under variant-only settings, and the author is never
 * asked it — so their row applies to nobody.
 *
 * `awaitsVerdict` is the server's per-recipient answer to "does this row still
 * owe this user a verdict" (`awaitsVariantVerdict`), never re-derived here.
 * Shared by both sides of the wire: the backend readers gate their surfaces on
 * it, and the survey dialog gates whether it may render a submittable form at
 * all — so a stranded author's row cannot be completed by a stale deep link.
 */ export function surveyApplies(toggles, awaitsVerdict) {
    return toggles.classic || awaitsVerdict;
}
/**
 * Whether a stored Variant Check answer ends the survey row on its own: with no
 * classic question switched on the verdict IS the survey, so any answer closes
 * the row — otherwise the recipient still owes the classic questions whatever
 * they said about the variant. A decline closes the row either way (D10), which
 * is the caller's own disjunct, not this one.
 */ export function variantAnswerCompletesSurvey(toggles) {
    return !toggles.classic;
}

//# sourceMappingURL=survey-toggles.js.map